import React, { PureComponent } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import axios from 'axios';
import { trackCustomEvent } from 'gatsby-plugin-google-analytics';

import Styles from './CommentsForm.styles';
import Form from './Form';

import Text from '../../base/Text';
import { getKey } from '../../../helpers';
import translationsOptions from './data/translations';


const Wrapper = styled.div`${Styles.wrapper}`;
const Title = styled(Text)`${Styles.title}`;
const MessageBox = styled.div`${Styles.messageBox}`;

/**
 * Field validation
 * @param {string} type of the field
 * @param {string} value of the field
 * @returns {boolean}
 */
const validateField = (type, value) => {
  switch (type) {
    case 'text':
      return value.replace(/^\s+$/gi, '').length >= 2;

    case 'tel':
      return /^[+#*\\[\]]*([0-9]*){6,45}$/.test(value) && value.length >= 8;

    case 'email':
      return value.match(/^([\w.%+-]+)@([\w-]+\.)+([\w]{2,})$/i);

    default:
      return false;
  }
};

/**
 * CommentsForm component
 * @returns {JSX}
 */
class CommentsForm extends PureComponent {
  static propTypes = {
    locale: PropTypes.string,
    endPoint: PropTypes.string,
    errorLabel: PropTypes.string,
    successLabel: PropTypes.string,
    title: PropTypes.string,
    instruction: PropTypes.string
  };

  /**
   * Constructor method
   * @param {Object} props of the component
   */
  constructor(props) {
    super(props);

    this.initialState = {
      success: false,
      error: false,
      translations: getKey(translationsOptions, props.locale),
      currentProgress: 0,
      disableButton: true,
      form: {
        name: {
          value: '',
          statu: 0
        },
        surnames: {
          value: '',
          status: 0
        },
        postalcode: {
          value: '',
          status: 0
        },
        email: {
          value: '',
          status: 0
        },
        subject: {
          value: '',
          status: 0
        },
        comment: {
          value: '',
          status: 0
        },
        subjectOptions: getKey(
          translationsOptions,
          `${props.locale}.subjects`,
          []
        ).map(item => ({ name: item.name, value: item.value }))
      }
    };
    this.state = Object.assign({}, this.initialState);
    this.requiredField = ['name', 'email', 'subject', 'comment'];
  }

  /**
   * handles form submit event
   * @param  {Event} event form submit event
   */
  onSubmit(event) {
    event.preventDefault();
    const { formActive } = this.state;
    if (!formActive) {
      this.setState({ formActive: true, disableButton: true });
    }
    this.sendData();
  }


  /**
   * areaHandler
   * @param {Object} event event of the change
   */
  areaHandler = (event) => {
    const { value } = event.target;
    const newState = Object.assign({}, this.state);
    const { form } = this.state;
    this.setState(
      Object.assign(newState, {
        form: {
          ...form,
          subject: {
            value,
            status: 1
          }
        }
      }),
      this.formProgress
    );
  };


  /**
   * Handle field change
   * @param {Object} event event of the change
   */
  handleChange = (event) => {
    const {
      target: { name, value, type }
    } = event;

    const newState = Object.assign({}, this.state);
    const { form } = this.state;
    switch (name) {
      default:
        Object.assign(newState, {
          form: {
            ...form,
            [name]: {
              value,
              status: validateField(type, value) ? 1 : 2
            }
          }
        });
        break;
    }

    this.setState(newState, this.formProgress);
  };

  /**
   * Sends the form throw the access point
   */
  sendData = () => {
    const { endPoint } = this.props;
    const { form } = this.state;
    const {
      name: { value: name },
      surnames: { value: surnames },
      postalcode: { value: postalcode },
      email: { value: email },
      subject: { value: subject },
      comment: { value: comment },
    } = form;
    trackCustomEvent({
      category: 'Contact Page',
      action: 'Send Button',
      label: 'Send Button Clicked',
    });
    axios.post(
      endPoint, JSON.stringify({
        name,
        surnames,
        postalcode,
        email,
        subject,
        comment
      })
    )
      .then(({ data }) => {
        if (!data.error) {
          trackCustomEvent({
            category: 'Contact Page',
            action: 'Send Contact Form Success',
            label: 'Send Contact Form Success',
          });
  
          this.setState({
            ...this.initialState,
            success: true
          });
        }
      })
      .catch(() => {
        trackCustomEvent({
          category: 'Contact Page',
          action: 'Send Contact Form Error / Failed',
          label: 'Contact Form Error',
        });
  
        this.setState({
          error: true
        });
      });
  }

  /**
   * Sets the form progress
   */
  formProgress = () => {
    const { currentProgress, form } = this.state;
    const completeSteps = this.requiredField.reduce(
      (acc, step) => {
        const obj = {};
        const valueStep = form[step].status; // 1 o 0
        obj[step] = { status: valueStep };

        return acc + (valueStep === 1 ? 1 : 0);
      }, 0
    );
    const progress = completeSteps / this.requiredField.length;
    const disableButton = progress < 1;
    if (progress !== currentProgress) {
      this.setState({
        currentProgress: progress,
        disableButton
      });
    }
  }

  /**
   * Success message
   * @returns {JSX}
   */
  renderSuccessMessage() {
    const { successLabel } = this.props;
    return (
      <MessageBox className="col-12">{successLabel}</MessageBox>
    );
  }

  /**
   * Error message
   * @returns {JSX}
   */
  renderErrorMessage() {
    const { errorLabel } = this.props;
    return (
      <MessageBox className="col-12">{errorLabel}</MessageBox>
    );
  }

  /**
   * Render the component
   * @returns {JSX}
   */
  render() {
    const {
      error,
      success,
      form,
      translations,
      disableButton
    } = this.state;
    const {
      title,
      instruction,
      containerId,
    } = this.props;
    return (
      <Wrapper id={containerId} className="container">
        <form onSubmit={e => this.onSubmit(e)}>
          <div className="row">
            <div className="col-md-12">
              <Title tag="h3" className="cbx-bold" style={{ color: '#272031', fontSize: 30, fontFamily: 'Gotham-Bold',  textAlign: 'center' }}>
                {title}
              </Title>
              <Title tag="h2" style={{ color: '#272031', fontSize: 20, fontFamily: 'Gotham-Book',  textAlign: 'center' }}>
                {instruction}
              </Title>
              {error && this.renderErrorMessage()}
              {success && this.renderSuccessMessage()}
              <Form
                translations={translations}
                areaHandler={e => this.areaHandler(e)}
                handleChange={e => this.handleChange(e)}
                handleSubmit={this.sendData}
                disableButton={disableButton}
                data={form}
              />
            </div>
          </div>
        </form>
      </Wrapper>
    );
  }
}


export default CommentsForm;
